/**
 * The edge node provisioning wizard — the node-side counterpart to CenterForm.
 *
 * A center is configuration the cloud owns, so its form saves desired state and
 * an apply makes it real. A node is a machine in a hospital that the cloud has
 * never met: it cannot be pushed to. So this form does the only thing that
 * works — it authors the node's ENTIRE center-config.yaml here, mints the
 * enrolment token, injects the token into the file, and hands back an install
 * bundle. The hospital runs the bundle; the node enrols itself.
 *
 * Every field of the edge repo's center-config.yaml is present, so a node can be
 * stood up without hand-editing YAML. Fields carry the help text that used to
 * live in that file's comments — the same relocation CenterForm made.
 *
 * The result is shown once, and says so: the rendered file contains a single-use
 * token and whatever passwords were typed, so from the moment it is generated
 * the file IS the secret. It is never re-fetchable.
 */

import { useState } from 'react';

import { Button } from './ui/Button';
import { Card } from './ui/Card';
import {
  Checkbox,
  NumberInput,
  Select,
  SelectItem,
  TextInput,
} from './ui/Form';
import { AddIcon, CopyIcon, DownloadIcon, TrashIcon } from './ui/Icon';

import { apiPost, describeError } from '../lib/api';
import { useResource } from '../lib/useResource';
import type {
  CenterListResponse,
  DateRestrictionInput,
  EdgeModalityInput,
  EdgeNodeConfigInput,
  EdgeNodeProvisionResponse,
  LabelRestrictionInput,
} from '../types/api';
import { Badge } from './Badge';
import { Callout } from './Callout';
import { RestrictionEditor } from './CenterForm';

/** Mirrors the server's EdgeNodeConfigInput defaults, which mirror the edge repo's. */
export const DEFAULT_EDGE_CONFIG: EdgeNodeConfigInput = {
  center_id: 0,
  center_name: 'Edge Node',
  display_name: 'Health 360 PACS Server',
  stable_age: 20,
  heartbeat_period: 60,
  storage_max_mb: 102400,
  cache_size_mb: 2428,
  cloud_url: '',
  cloud_username: '',
  cloud_password: '',
  pg_database: 'orthanc',
  pg_username: 'orthanc',
  pg_password: '',
  fleet_ingest_url: 'https://pacs-api.health-360.co',
  modalities: [],
  ingest_transcoding: '',
  transcode_uncompressed: false,
  transcode_compressed: false,
  retention_enabled: false,
  retention_days: 60,
  retention_require_replicated: true,
  retention_dry_run: false,
  retention_batch: 100,
  retention_interval_heartbeats: 720,
  repl_compress: true,
  repl_max_retries: 3,
  repl_sync_on_startup: false,
  repl_allowed_modalities: null,
  repl_retry_base_delay: 60,
  repl_retry_max_delay: 3600,
  repl_recheck_interval: 60,
  repl_small_threshold: 50,
  repl_medium_threshold: 200,
  label_sync_enabled: false,
  label_sync_interval_heartbeats: 5,
  h360_enabled: false,
  h360_url: '',
  h360_tenant_id: '',
  h360_webhook_path: '/webhooks/pacs360/study-available',
  h360_retry_interval: 300,
  viewer_enabled: false,
  viewer_image: 'ghcr.io/health-360-ai/health360-viewer:latest',
  cloud_api_enabled: false,
  cloud_api_url: '',
  cloud_api_gateway: '',
  cloud_api_username: '',
  cloud_api_password: '',
  cloud_api_workspace_id: '',
  hamsa_enabled: false,
  hamsa_share_path: '',
  hamsa_share_local_path: '',
  hamsa_share_username: '',
  hamsa_share_password: '',
  hamsa_api_gateway: '',
  hamsa_api_base: '',
  hamsa_api_username: '',
  hamsa_api_password: '',
  hamsa_poll_interval: 30,
  hamsa_pacs_api: '',
  hamsa_workspace_id: '',
  hamsa_insurance_reports_interval: 300,
  assign_enabled: false,
  assign_api_gateway: '',
  assign_api_base: '',
  assign_api_username: '',
  assign_api_password: '',
  assign_workspace_id: '',
  assign_interval: 300,
  date_restrictions: [],
  label_restrictions: [],
};

export function EdgeNodeWizard({ onProvisioned }: { onProvisioned: () => void }) {
  const [label, setLabel] = useState('');
  const [hospital, setHospital] = useState('');
  const [centerSlug, setCenterSlug] = useState('');
  const [expiresHours, setExpiresHours] = useState(24);
  const [centerIdTouched, setCenterIdTouched] = useState(false);
  const [cfg, setCfg] = useState<EdgeNodeConfigInput>(DEFAULT_EDGE_CONFIG);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EdgeNodeProvisionResponse | null>(null);

  const centersResource = useResource<CenterListResponse>('/api/centers');
  const centers = centersResource.data?.centers ?? [];

  function patch(p: Partial<EdgeNodeConfigInput>) {
    setCfg((prev) => ({ ...prev, ...p }));
  }

  // Picking a center pre-fills the two fields it determines, so the common case
  // is a slug and a label. The server still has the final say on center_id.
  function pickCenter(slug: string) {
    setCenterSlug(slug);
    const center = centers.find((c) => c.slug === slug);
    if (!center) return;
    const doctorId = Number(center.domain_prefix);
    patch({
      ...(Number.isFinite(doctorId) && doctorId > 0 ? { center_id: doctorId } : {}),
      cloud_url: `https://${center.domain}`,
      center_name: center.name || center.slug,
    });
  }

  async function provision() {
    setBusy(true);
    setError(null);
    try {
      const response = await apiPost<EdgeNodeProvisionResponse>(
        '/api/edge-nodes/provision',
        {
          label: label.trim(),
          center_slug: centerSlug || null,
          hospital: hospital.trim() || null,
          expires_hours: expiresHours,
          config: cfg,
        },
      );
      setResult(response);
      onProvisioned();
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    return <ProvisionResult result={result} onDone={() => { setResult(null); setLabel(''); }} />;
  }

  // Mirrors the ingest's own rule (public subdomain {id}.host, or .../pacs/{id}).
  // A URL carrying no doctor identity returns null and is not a mismatch — an
  // internal hostname or proxy is legitimate and only the cloud can resolve it.
  const cloudUrlDoctorMismatch = (() => {
    if (!centerSlug || cfg.center_id < 1) return null;
    const raw = cfg.cloud_url.trim();
    if (!raw) return null;
    let parsed: URL;
    try {
      parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
    } catch {
      return null;
    }
    const segments = parsed.pathname.split('/').filter(Boolean);
    const pacsAt = segments.indexOf('pacs');
    const fromPath =
      pacsAt >= 0 && segments[pacsAt + 1] && /^\d+$/.test(segments[pacsAt + 1] as string)
        ? Number(segments[pacsAt + 1])
        : null;
    const firstLabel = parsed.hostname.split('.')[0] ?? '';
    const fromHost = /^\d+$/.test(firstLabel) ? Number(firstLabel) : null;
    const urlDoctor = fromPath ?? fromHost;
    return urlDoctor != null && urlDoctor !== cfg.center_id ? urlDoctor : null;
  })();

  // The same two rules the server enforces, checked here so the form does not
  // send a request it knows will be refused. An unrecognised mode or a missing
  // days/date bound is not cosmetic: on the node the first fails OPEN (no date
  // filter at all) and the second stops generate.py writing any file.
  const restrictionProblem = (() => {
    for (const d of cfg.date_restrictions) {
      if (d.mode === 'last_days' && (d.days == null || d.days < 1)) {
        return `Date restriction for ${d.aet || 'an AE title'} uses "last_days" with no day count.`;
      }
      if (d.mode === 'date_range' && !(d.start_date && d.end_date)) {
        return `Date restriction for ${d.aet || 'an AE title'} uses "date_range" without both dates.`;
      }
    }
    for (const l of cfg.label_restrictions) {
      if (l.labels.length === 0) {
        return `Label restriction for ${l.aet || 'an AE title'} names no labels, so it restricts nothing.`;
      }
    }
    return null;
  })();

  const canSubmit =
    label.trim().length > 0 &&
    cfg.cloud_url.trim().length > 0 &&
    cfg.center_id > 0 &&
    cloudUrlDoctorMismatch == null &&
    restrictionProblem == null;

  return (
    <div className="stack">
      {error ? (
        <Callout tone="critical" title="Could not provision">
          {error}
        </Callout>
      ) : null}

      <Card className="stack">
        <h2 className="section__title">Enrolment</h2>
        <p className="field__help muted">
          The token is minted when you submit and baked into the generated config. Owner
          role required.
        </p>
        <TextInput
          id="edge-label"
          labelText="Label"
          helperText="How this node is named in the panel. Shown on the edge fleet table."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="City Hospital basement node"
        />
        <TextInput
          id="edge-hospital"
          labelText="Hospital"
          helperText="Free text, shown under the label. Optional."
          value={hospital}
          onChange={(e) => setHospital(e.target.value)}
          placeholder="City General Hospital"
        />
        <Select
          id="edge-center"
          labelText="Pre-assign center"
          helperText="Binds the node to that center's doctor id at enrolment (Contract A) and pre-fills the cloud URL below. Without one, linkage is guessed from the node's cloud peer hostname."
          value={centerSlug}
          onChange={(e) => pickCenter(e.target.value)}
        >
          <SelectItem value="" text="None — link from hostname" />
          {centers.map((center) => (
            <SelectItem
              key={center.slug}
              value={center.slug}
              text={`${center.slug} — ${center.domain}`}
            />
          ))}
        </Select>
        <NumberInput
          id="edge-expires"
          label="Token expires (hours)"
          helperText="Unused tokens expire. Keep this short — it only has to survive the trip to the hospital."
          min={1}
          max={336}
          value={expiresHours}
          onChange={(_e, { value }: { value: number | string }) =>
            setExpiresHours(Math.max(1, Math.min(336, Number(value) || 24)))
          }
        />
      </Card>

      <Card className="stack">
        <h2 className="section__title">Identity</h2>
        <NumberInput
          id="edge-center-id"
          label="Center id (doctor id)"
          helperText="Contract A: this IS the doctor id in the shared clinical database. Set automatically when a center is pre-assigned above, and the server re-asserts it so the two cannot diverge."
          // min is 0, not 1, so an untouched form does not open already red. The
          // real requirement is enforced by `invalid` below and by the submit
          // guard — an empty form is incomplete, not wrong.
          min={0}
          invalid={centerIdTouched && cfg.center_id < 1}
          invalidText="Must be the center's doctor id — a positive integer."
          value={cfg.center_id}
          onChange={(_e, { value }: { value: number | string }) => {
            setCenterIdTouched(true);
            patch({ center_id: Number(value) || 0 });
          }}
        />
        <TextInput
          id="edge-center-name"
          labelText="Center name"
          helperText="Human name for this node's center, written into the config header."
          value={cfg.center_name}
          onChange={(e) => patch({ center_name: e.target.value })}
        />
        <TextInput
          id="edge-display-name"
          labelText="Orthanc display name"
          helperText="Orthanc's `Name` — what the local Orthanc UI and DICOM peers show."
          value={cfg.display_name}
          onChange={(e) => patch({ display_name: e.target.value })}
        />
      </Card>

      <Card className="stack">
        <h2 className="section__title">Server &amp; storage</h2>
        <div className="row-editor">
          <NumberInput
            id="edge-stable-age"
            label="Stable age (s)"
            helperText="Seconds of quiet before a study counts as complete and may replicate."
            min={0}
            value={cfg.stable_age}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ stable_age: Number(value) })
            }
          />
          <NumberInput
            id="edge-heartbeat"
            label="Heartbeat period (s)"
            helperText="How often the agent reports in. Liveness is judged against this."
            min={5}
            value={cfg.heartbeat_period}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ heartbeat_period: Number(value) })
            }
          />
        </div>
        <div className="row-editor">
          <NumberInput
            id="edge-storage-max"
            label="Max storage (MB)"
            helperText="Orthanc's MaximumStorageSize — the disk budget for local pixel data."
            min={1}
            value={cfg.storage_max_mb}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ storage_max_mb: Number(value) })
            }
          />
          <NumberInput
            id="edge-cache"
            label="Storage cache (MB)"
            helperText="MaximumStorageCacheSize. Sized against the node's RAM, not its disk."
            min={1}
            value={cfg.cache_size_mb}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ cache_size_mb: Number(value) })
            }
          />
        </div>
      </Card>

      <Card className="stack">
        <h2 className="section__title">Cloud replication target</h2>
        <p className="field__help muted">
          The cloud Orthanc this node pushes studies up to, registered as the{' '}
          <code>Health360_Cloud</code> peer. Credentials go into the node&rsquo;s{' '}
          <code>.env</code>; they are echoed back in the generated file, which is why that
          file is a secret.
        </p>
        {cloudUrlDoctorMismatch != null ? (
          <Callout tone="critical" title="This URL points at a different center">
            The URL names doctor <code>{cloudUrlDoctorMismatch}</code>, but this node is
            being assigned to <code>{centerSlug}</code> (doctor{' '}
            <code>{cfg.center_id}</code>). Studies from this hospital would replicate into
            the wrong center&rsquo;s cloud PACS, and no panel view would show it. The
            server refuses this combination.
          </Callout>
        ) : null}
        <TextInput
          id="edge-cloud-url"
          labelText="Cloud Orthanc URL"
          helperText="Pre-filled from the assigned center. Include the scheme and port if non-standard. This is the field that actually binds the node to a center — the agent reports it, and the cloud reads a doctor id back out of it."
          value={cfg.cloud_url}
          onChange={(e) => patch({ cloud_url: e.target.value })}
          placeholder="https://1908.health-360.co"
          invalid={cloudUrlDoctorMismatch != null}
          invalidText="Points at a different center than the one assigned above."
        />
        <div className="row-editor">
          <TextInput
            id="edge-cloud-user"
            labelText="Cloud username"
            value={cfg.cloud_username}
            onChange={(e) => patch({ cloud_username: e.target.value })}
          />
          <TextInput
            id="edge-cloud-pass"
            labelText="Cloud password"
            type="password"
            value={cfg.cloud_password}
            onChange={(e) => patch({ cloud_password: e.target.value })}
          />
        </div>
      </Card>

      <Card className="stack">
        <h2 className="section__title">Node Postgres</h2>
        <p className="field__help muted">
          The node&rsquo;s own index database, running in its compose project. Not the cloud
          database — nothing here reaches the shared cluster.
        </p>
        <div className="row-editor">
          <TextInput
            id="edge-pg-db"
            labelText="Database"
            value={cfg.pg_database}
            onChange={(e) => patch({ pg_database: e.target.value })}
          />
          <TextInput
            id="edge-pg-user"
            labelText="Username"
            value={cfg.pg_username}
            onChange={(e) => patch({ pg_username: e.target.value })}
          />
          <TextInput
            id="edge-pg-pass"
            labelText="Password"
            type="password"
            value={cfg.pg_password}
            onChange={(e) => patch({ pg_password: e.target.value })}
          />
        </div>
      </Card>

      <Card className="stack">
        <h2 className="section__title">Fleet agent</h2>
        <TextInput
          id="edge-ingest"
          labelText="Ingest URL"
          helperText="Where heartbeats go. The node must be able to reach this from inside the hospital network."
          value={cfg.fleet_ingest_url}
          onChange={(e) => patch({ fleet_ingest_url: e.target.value })}
        />
      </Card>

      <EdgeModalityEditor
        value={cfg.modalities}
        onChange={(modalities) => patch({ modalities })}
      />

      <RestrictionEditor
        dates={cfg.date_restrictions}
        labels={cfg.label_restrictions}
        onDates={(date_restrictions: DateRestrictionInput[]) => patch({ date_restrictions })}
        onLabels={(label_restrictions: LabelRestrictionInput[]) => patch({ label_restrictions })}
      />

      <Card className="stack">
        <h2 className="section__title">Transcoding</h2>
        <p className="field__help muted">
          Leave the transfer syntax blank to store instances exactly as received. Setting it
          makes Orthanc re-encode on ingest — CPU cost per study, in exchange for smaller
          storage.
        </p>
        <TextInput
          id="edge-transcode-ts"
          labelText="Ingest transfer syntax"
          helperText="e.g. 1.2.840.10008.1.2.4.90 (JPEG 2000 lossless). Blank = store as-is."
          value={cfg.ingest_transcoding}
          onChange={(e) => patch({ ingest_transcoding: e.target.value })}
          placeholder="(blank = no transcoding)"
        />
        <Checkbox
          id="edge-transcode-uncompressed"
          labelText="Transcode uncompressed instances"
          checked={cfg.transcode_uncompressed}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ transcode_uncompressed: checked })
          }
        />
        <Checkbox
          id="edge-transcode-compressed"
          labelText="Transcode already-compressed instances"
          checked={cfg.transcode_compressed}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ transcode_compressed: checked })
          }
        />
      </Card>

      <Card className="stack">
        <h2 className="section__title">Replication</h2>
        <p className="field__help muted">
          How studies get to the cloud, and how failures are retried. The thresholds pick a
          batch size by study size so a large study cannot starve the queue.
        </p>
        <Checkbox
          id="edge-repl-compress"
          labelText="Compress on transfer"
          checked={cfg.repl_compress}
          onChange={(_e, { checked }: { checked: boolean }) => patch({ repl_compress: checked })}
        />
        <Checkbox
          id="edge-repl-sync-startup"
          labelText="Sync unreplicated studies on startup"
          checked={cfg.repl_sync_on_startup}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ repl_sync_on_startup: checked })
          }
        />
        <TextInput
          id="edge-repl-allowed"
          labelText="Allowed modalities"
          helperText="Comma-separated DICOM modality codes (CT, MR, …) to replicate. Blank = all."
          value={(cfg.repl_allowed_modalities ?? []).join(', ')}
          onChange={(e) => {
            const parts = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
            patch({ repl_allowed_modalities: parts.length ? parts : null });
          }}
          placeholder="(blank = all modalities)"
        />
        <div className="row-editor">
          <NumberInput
            id="edge-repl-retries"
            label="Max retries"
            min={0}
            value={cfg.repl_max_retries}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_max_retries: Number(value) })
            }
          />
          <NumberInput
            id="edge-repl-base-delay"
            label="Retry base delay (s)"
            min={1}
            value={cfg.repl_retry_base_delay}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_retry_base_delay: Number(value) })
            }
          />
          <NumberInput
            id="edge-repl-max-delay"
            label="Retry max delay (s)"
            min={1}
            value={cfg.repl_retry_max_delay}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_retry_max_delay: Number(value) })
            }
          />
        </div>
        <div className="row-editor">
          <NumberInput
            id="edge-repl-recheck"
            label="Recheck interval (s)"
            min={1}
            value={cfg.repl_recheck_interval}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_recheck_interval: Number(value) })
            }
          />
          <NumberInput
            id="edge-repl-small"
            label="Small study threshold"
            min={0}
            value={cfg.repl_small_threshold}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_small_threshold: Number(value) })
            }
          />
          <NumberInput
            id="edge-repl-medium"
            label="Medium study threshold"
            min={0}
            value={cfg.repl_medium_threshold}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ repl_medium_threshold: Number(value) })
            }
          />
        </div>
      </Card>

      <Card className="stack">
        <h2 className="section__title">Retention (auto-cleanup)</h2>
        <Callout tone="warning" title="This deletes studies from the node">
          Cleanup removes local studies once they are old enough. Leave{' '}
          <em>require replicated</em> on: without it, a study can be deleted from the
          hospital before the cloud ever received it. Use dry run first.
        </Callout>
        <Checkbox
          id="edge-retention-enabled"
          labelText="Enable auto-cleanup"
          checked={cfg.retention_enabled}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ retention_enabled: checked })
          }
        />
        {cfg.retention_enabled ? (
          <>
            <Checkbox
              id="edge-retention-replicated"
              labelText="Only delete studies confirmed replicated to the cloud"
              checked={cfg.retention_require_replicated}
              onChange={(_e, { checked }: { checked: boolean }) =>
                patch({ retention_require_replicated: checked })
              }
            />
            <Checkbox
              id="edge-retention-dryrun"
              labelText="Dry run (log what would be deleted, delete nothing)"
              checked={cfg.retention_dry_run}
              onChange={(_e, { checked }: { checked: boolean }) =>
                patch({ retention_dry_run: checked })
              }
            />
            <div className="row-editor">
              <NumberInput
                id="edge-retention-days"
                label="Keep for (days)"
                min={1}
                value={cfg.retention_days}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ retention_days: Number(value) })
                }
              />
              <NumberInput
                id="edge-retention-batch"
                label="Batch size"
                min={1}
                value={cfg.retention_batch}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ retention_batch: Number(value) })
                }
              />
              <NumberInput
                id="edge-retention-interval"
                label="Run every N heartbeats"
                min={1}
                value={cfg.retention_interval_heartbeats}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ retention_interval_heartbeats: Number(value) })
                }
              />
            </div>
          </>
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Label sync</h2>
        <p className="field__help muted">
          Pulls Orthanc labels down from the cloud so label-restricted C-FIND rules on this
          node stay in step with the center.
        </p>
        <Checkbox
          id="edge-labelsync-enabled"
          labelText="Enable label sync"
          checked={cfg.label_sync_enabled}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ label_sync_enabled: checked })
          }
        />
        {cfg.label_sync_enabled ? (
          <NumberInput
            id="edge-labelsync-interval"
            label="Sync every N heartbeats"
            min={1}
            value={cfg.label_sync_interval_heartbeats}
            onChange={(_e, { value }: { value: number | string }) =>
              patch({ label_sync_interval_heartbeats: Number(value) })
            }
          />
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Local viewer</h2>
        <Checkbox
          id="edge-viewer-enabled"
          labelText="Run the bundled OHIF viewer on the node"
          checked={cfg.viewer_enabled}
          onChange={(_e, { checked }: { checked: boolean }) => patch({ viewer_enabled: checked })}
        />
        {cfg.viewer_enabled ? (
          <TextInput
            id="edge-viewer-image"
            labelText="Viewer image"
            helperText="Private registry — the node needs credentials to pull it."
            value={cfg.viewer_image}
            onChange={(e) => patch({ viewer_image: e.target.value })}
          />
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Hospital360 webhook</h2>
        <p className="field__help muted">
          Notifies the hospital&rsquo;s own system when a study becomes available locally.
        </p>
        <Checkbox
          id="edge-h360-enabled"
          labelText="Enable Hospital360 notifications"
          checked={cfg.h360_enabled}
          onChange={(_e, { checked }: { checked: boolean }) => patch({ h360_enabled: checked })}
        />
        {cfg.h360_enabled ? (
          <>
            <TextInput
              id="edge-h360-url"
              labelText="Base URL"
              value={cfg.h360_url}
              onChange={(e) => patch({ h360_url: e.target.value })}
            />
            <div className="row-editor">
              <TextInput
                id="edge-h360-tenant"
                labelText="Tenant id"
                value={cfg.h360_tenant_id}
                onChange={(e) => patch({ h360_tenant_id: e.target.value })}
              />
              <TextInput
                id="edge-h360-path"
                labelText="Webhook path"
                value={cfg.h360_webhook_path}
                onChange={(e) => patch({ h360_webhook_path: e.target.value })}
              />
              <NumberInput
                id="edge-h360-retry"
                label="Retry interval (s)"
                min={1}
                value={cfg.h360_retry_interval}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ h360_retry_interval: Number(value) })
                }
              />
            </div>
          </>
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Cloud API</h2>
        <Checkbox
          id="edge-cloudapi-enabled"
          labelText="Enable cloud API integration"
          checked={cfg.cloud_api_enabled}
          onChange={(_e, { checked }: { checked: boolean }) =>
            patch({ cloud_api_enabled: checked })
          }
        />
        {cfg.cloud_api_enabled ? (
          <>
            <div className="row-editor">
              <TextInput
                id="edge-cloudapi-url"
                labelText="PACS API base"
                value={cfg.cloud_api_url}
                onChange={(e) => patch({ cloud_api_url: e.target.value })}
              />
              <TextInput
                id="edge-cloudapi-gateway"
                labelText="API gateway"
                value={cfg.cloud_api_gateway}
                onChange={(e) => patch({ cloud_api_gateway: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <TextInput
                id="edge-cloudapi-user"
                labelText="Username"
                value={cfg.cloud_api_username}
                onChange={(e) => patch({ cloud_api_username: e.target.value })}
              />
              <TextInput
                id="edge-cloudapi-pass"
                labelText="Password"
                type="password"
                value={cfg.cloud_api_password}
                onChange={(e) => patch({ cloud_api_password: e.target.value })}
              />
              <TextInput
                id="edge-cloudapi-ws"
                labelText="Workspace id"
                value={cfg.cloud_api_workspace_id}
                onChange={(e) => patch({ cloud_api_workspace_id: e.target.value })}
              />
            </div>
          </>
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">HAMSA (HIS / insurance)</h2>
        <Checkbox
          id="edge-hamsa-enabled"
          labelText="Enable HAMSA integration"
          checked={cfg.hamsa_enabled}
          onChange={(_e, { checked }: { checked: boolean }) => patch({ hamsa_enabled: checked })}
        />
        {cfg.hamsa_enabled ? (
          <>
            <div className="row-editor">
              <TextInput
                id="edge-hamsa-share"
                labelText="Share path (UNC)"
                value={cfg.hamsa_share_path}
                onChange={(e) => patch({ hamsa_share_path: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-share-local"
                labelText="Share local mount path"
                value={cfg.hamsa_share_local_path}
                onChange={(e) => patch({ hamsa_share_local_path: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <TextInput
                id="edge-hamsa-share-user"
                labelText="Share username"
                value={cfg.hamsa_share_username}
                onChange={(e) => patch({ hamsa_share_username: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-share-pass"
                labelText="Share password"
                type="password"
                value={cfg.hamsa_share_password}
                onChange={(e) => patch({ hamsa_share_password: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <TextInput
                id="edge-hamsa-gateway"
                labelText="API gateway"
                value={cfg.hamsa_api_gateway}
                onChange={(e) => patch({ hamsa_api_gateway: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-base"
                labelText="API base"
                value={cfg.hamsa_api_base}
                onChange={(e) => patch({ hamsa_api_base: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-pacs"
                labelText="PACS API"
                value={cfg.hamsa_pacs_api}
                onChange={(e) => patch({ hamsa_pacs_api: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <TextInput
                id="edge-hamsa-user"
                labelText="API username"
                value={cfg.hamsa_api_username}
                onChange={(e) => patch({ hamsa_api_username: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-pass"
                labelText="API password"
                type="password"
                value={cfg.hamsa_api_password}
                onChange={(e) => patch({ hamsa_api_password: e.target.value })}
              />
              <TextInput
                id="edge-hamsa-ws"
                labelText="Workspace id"
                value={cfg.hamsa_workspace_id}
                onChange={(e) => patch({ hamsa_workspace_id: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <NumberInput
                id="edge-hamsa-poll"
                label="Poll interval (s)"
                min={1}
                value={cfg.hamsa_poll_interval}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ hamsa_poll_interval: Number(value) })
                }
              />
              <NumberInput
                id="edge-hamsa-reports"
                label="Insurance reports interval (s)"
                min={1}
                value={cfg.hamsa_insurance_reports_interval}
                onChange={(_e, { value }: { value: number | string }) =>
                  patch({ hamsa_insurance_reports_interval: Number(value) })
                }
              />
            </div>
          </>
        ) : null}
      </Card>

      <Card className="stack">
        <h2 className="section__title">Assignment sync</h2>
        <p className="field__help muted">
          Pulls radiologist study assignments down so the node can label studies locally.
        </p>
        <Checkbox
          id="edge-assign-enabled"
          labelText="Enable assignment sync"
          checked={cfg.assign_enabled}
          onChange={(_e, { checked }: { checked: boolean }) => patch({ assign_enabled: checked })}
        />
        {cfg.assign_enabled ? (
          <>
            <div className="row-editor">
              <TextInput
                id="edge-assign-gateway"
                labelText="API gateway"
                value={cfg.assign_api_gateway}
                onChange={(e) => patch({ assign_api_gateway: e.target.value })}
              />
              <TextInput
                id="edge-assign-base"
                labelText="API base"
                value={cfg.assign_api_base}
                onChange={(e) => patch({ assign_api_base: e.target.value })}
              />
            </div>
            <div className="row-editor">
              <TextInput
                id="edge-assign-user"
                labelText="Username"
                value={cfg.assign_api_username}
                onChange={(e) => patch({ assign_api_username: e.target.value })}
              />
              <TextInput
                id="edge-assign-pass"
                labelText="Password"
                type="password"
                value={cfg.assign_api_password}
                onChange={(e) => patch({ assign_api_password: e.target.value })}
              />
              <TextInput
                id="edge-assign-ws"
                labelText="Workspace id"
                value={cfg.assign_workspace_id}
                onChange={(e) => patch({ assign_workspace_id: e.target.value })}
              />
            </div>
            <NumberInput
              id="edge-assign-interval"
              label="Sync interval (s)"
              min={1}
              value={cfg.assign_interval}
              onChange={(_e, { value }: { value: number | string }) =>
                patch({ assign_interval: Number(value) })
              }
            />
          </>
        ) : null}
      </Card>

      <Card className="stack">
        <Callout tone="info" title="This mints a token and renders the node's whole config">
          Submitting creates a single-use enrolment token and returns a complete{' '}
          <code>center-config.yaml</code> with the token already in it. Nothing on the
          hospital node changes until someone runs the install bundle there.
        </Callout>
        {restrictionProblem ? (
          <Callout tone="warning" title="A C-FIND restriction is incomplete">
            {restrictionProblem} Fix it above — left as is, it would silently fail open or
            stop the node&rsquo;s generator from producing any files.
          </Callout>
        ) : !canSubmit ? (
          <p className="muted">
            A label, a center id and a cloud URL are required. Pre-assign a center to fill
            the last two automatically.
          </p>
        ) : null}
        <div>
          <Button onClick={provision} disabled={busy || !canSubmit}>
            {busy ? 'Provisioning…' : 'Mint token + generate install bundle'}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ── modalities (edge shape: no symbolic name — the AE title is the key) ───────

function EdgeModalityEditor({
  value,
  onChange,
}: {
  value: EdgeModalityInput[];
  onChange: (m: EdgeModalityInput[]) => void;
}) {
  function update(i: number, patch: Partial<EdgeModalityInput>) {
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  return (
    <Card className="stack">
      <div className="toolbar">
        <h2 className="section__title">
          DICOM modalities <Badge tone="neutral">{value.length}</Badge>
        </h2>
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={AddIcon}
          onClick={() => onChange([...value, { aet: '', host: '*', port: 104 }])}
        >
          Add modality
        </Button>
      </div>
      <p className="field__help muted">
        Scanners and workstations on the hospital network this node will talk to. The AE
        title is the key — on a node there is no separate symbolic name.
      </p>
      {value.map((m, i) => (
        <div key={i} className="row-editor">
          <TextInput
            id={`edge-modality-${i}-aet`}
            labelText="AE title"
            hideLabel
            placeholder="AE title"
            value={m.aet}
            onChange={(e) => update(i, { aet: e.target.value })}
          />
          <TextInput
            id={`edge-modality-${i}-host`}
            labelText="Host (* = any)"
            hideLabel
            placeholder="Host (* = any)"
            value={m.host}
            onChange={(e) => update(i, { host: e.target.value })}
          />
          <NumberInput
            id={`edge-modality-${i}-port`}
            label="Port"
            hideLabel
            value={m.port}
            onChange={(_e, { value: val }: { value: number | string }) =>
              update(i, { port: Number(val) })
            }
          />
          <Button
            kind="ghost"
            size="sm"
            renderIcon={TrashIcon}
            hasIconOnly
            iconDescription="Remove"
            onClick={() => onChange(value.filter((_, idx) => idx !== i))}
          />
        </div>
      ))}
    </Card>
  );
}

// ── the result: shown once ───────────────────────────────────────────────────

function ProvisionResult({
  result,
  onDone,
}: {
  result: EdgeNodeProvisionResponse;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);

  function copy(what: string, text: string) {
    void navigator.clipboard?.writeText(text).then(
      () => setCopied(what),
      () => setCopied(null),
    );
  }

  function download() {
    const blob = new Blob([result.config_yaml], { type: 'application/yaml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.config_filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="stack">
      <Callout tone="warning" title="Copy this now — it is shown once">
        The generated file contains the single-use enrolment token and every password you
        entered. The server keeps only a hash of the token, and this file is never
        re-fetchable. Treat it as the secret it is: move it to the node over a trusted
        channel and do not leave it in a downloads folder.
      </Callout>

      <Card className="stack">
        <h2 className="section__title">Node</h2>
        <dl className="kv">
          <dt>Label</dt>
          <dd>{result.label}</dd>
          <dt>Doctor id</dt>
          <dd>
            {result.doctor_id != null ? (
              <code>{result.doctor_id}</code>
            ) : (
              <span className="muted">unassigned — will link from hostname</span>
            )}
          </dd>
          <dt>Token expires in</dt>
          <dd>{result.expires_hours} hours</dd>
          <dt>Token</dt>
          <dd>
            <code className="secret">{result.token}</code>
          </dd>
        </dl>
        <p className="muted">{result.usage}</p>
      </Card>

      <Card className="stack">
        <div className="toolbar">
          <h2 className="section__title">{result.config_filename}</h2>
          <div className="toolbar">
            <Button kind="tertiary" size="sm" renderIcon={DownloadIcon} onClick={download}>
              Download
            </Button>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={CopyIcon}
              onClick={() => copy('config', result.config_yaml)}
            >
              {copied === 'config' ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
        <pre className="code-block">{result.config_yaml}</pre>
      </Card>

      <Card className="stack">
        <div className="toolbar">
          <h2 className="section__title">1. Prepare the host</h2>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={CopyIcon}
            onClick={() => copy('bootstrap', result.bootstrap_script)}
          >
            {copied === 'bootstrap' ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <p className="muted">
          Packages, Docker, the <code>fleet</code> user and its SSH key,
          wake-on-LAN, the network and the mesh. Idempotent — safe to re-run
          after a failure. The network step uses <code>netplan try</code>, which
          reverts by itself if the new configuration takes the machine off the
          network.
        </p>
        {result.bootstrap_warning ? (
          <Callout tone="warning" title="No SSH key will be authorised">
            {result.bootstrap_warning}
          </Callout>
        ) : null}
        <pre className="code-block">{result.bootstrap_script}</pre>
      </Card>

      <Card className="stack">
        <div className="toolbar">
          <h2 className="section__title">2. Install the node</h2>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={CopyIcon}
            onClick={() => copy('install', result.install_script)}
          >
            {copied === 'install' ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <pre className="code-block">{result.install_script}</pre>
      </Card>

      <div>
        <Button kind="tertiary" onClick={onDone}>
          Done — hide the token
        </Button>
      </div>
    </div>
  );
}
